import { Alert, AlertTitle, Box, Grid, MenuItem, Tab, Tabs, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllWebForms } from 'src/shared/services/webFormService';
import { WebForm } from 'src/modules/webforms/models/webForm';
import { WebFormConfigurator } from 'src/modules/webforms/components/webFormConfigurator';
import { WebFormEmbedCode } from 'src/modules/webforms/components/webFormEmbedCode';
import { WebFormInlineSettings } from 'src/modules/webforms/components/webFormInlineSettings';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';

export const MemberRegistryWebFormsPage = () => {
  const { t } = useTranslation(['webforms', 'settings']);
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();
  const canEdit = getPermission(currentUser, PermissionEnum.MemberRegWebFormUpdate);
  const [webForms, setWebForms] = useState<WebForm[]>([]);
  const [selectedFormName, setSelectedFormName] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const fetchWebForms = useCallback(async () => {
    try {
      const res = await getAllWebForms({ filter: 'webform', pageIndex: 0, pageSize: 100, sortCol: 'formName', sortDir: 'asc' });
      if (isMounted()) {
        setWebForms(res.data.results || []);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchWebForms();
  }, [fetchWebForms]);

  const selectedWebForm = webForms.find((wf) => wf.formName === selectedFormName);

  return (
    <Box>
      <PageFilterSection
        leftContent={
          <TextField
            select
            size='small'
            value={selectedFormName}
            onChange={(e) => {
              setSelectedFormName(e.target.value);
              setActiveTab(0);
            }}
            sx={{ minWidth: 250 }}
            label={t('webforms:selectWebFormLabel')}
          >
            {webForms.map((wf) => (
              <MenuItem key={wf.formName} value={wf.formName}>
                {wf.formDescription || wf.formName}
              </MenuItem>
            ))}
          </TextField>
        }
      />

      {!selectedFormName && (
        <Alert severity='info'>
          <AlertTitle>{t('webforms:selectWebFormLabel')}</AlertTitle>
          {t('webforms:selectWebForm')}
        </Alert>
      )}

      {selectedFormName && (
        <>
          {canEdit && <Alert severity='warning'>{t('settings:memberRegistry.settingsWarning')}</Alert>}
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            sx={{ borderBottom: 1, borderColor: 'divider', '& button': { textTransform: 'none' } }}
          >
            <Tab label={t('settings:memberRegistry.tabForms')} />
            <Tab label={t('webforms:settings')} />
            <Tab label={t('webforms:embededCode')} />
          </Tabs>

          {activeTab === 0 && <WebFormConfigurator formName={selectedFormName} readOnly={!canEdit} />}
          {activeTab === 1 && selectedWebForm && <WebFormInlineSettings webForm={selectedWebForm} onSaved={fetchWebForms} readOnly={!canEdit} />}
          {activeTab === 2 && (
            <Grid container spacing={2} sx={{ p: 3 }}>
              <WebFormEmbedCode webFormName={selectedFormName} />
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};
